import React from 'react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Experience', href: '#experience' },
    { name: 'Projects', href: '#projects' },
    { name: 'Resume', href: '#resume' },
    { name: 'Contact', href: '#contact' }
  ];

  const socialLinks = [
    {
      name: 'GitHub',
      icon: 'fab fa-github',
      url: 'https://github.com/SilentMic',
      color: 'hover:text-gray-300'
    },
    {
      name: 'LinkedIn',
      icon: 'fab fa-linkedin',
      url: 'https://www.linkedin.com/in/kiratmehta/',
      color: 'hover:text-blue-400'
    },
    {
      name: 'Email',
      icon: 'fas fa-envelope',
      url: 'mailto:kiratmehta92@gmail.com',
      color: 'hover:text-red-400'
    }
  ];

  const handleNavClick = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-900 border-t border-slate-800 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="md:col-span-2">
            <div className="mb-4">
              <h3 className="text-2xl font-bold">
                <span className="gradient-text">Kirat Mehta</span>
              </h3>
              <p className="text-gray-400 mt-2">
                Technical Support Analyst & Software Developer
              </p>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6 max-w-md text-sm">
              Passionate about creating efficient solutions using modern technologies. 
              Specializing in C#, SQL, React, and AI tools to solve complex problems 
              and deliver exceptional user experiences.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target={social.url.startsWith('http') ? '_blank' : '_self'}
                  rel={social.url.startsWith('http') ? 'noopener noreferrer' : ''}
                  className={`social-link w-11 h-11 bg-slate-800 rounded-lg flex items-center justify-center text-gray-400 transition-all duration-300 ${social.color} hover:scale-110`}
                  aria-label={social.name}
                >
                  <i className={`${social.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-lg">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleNavClick(link.href)}
                    className="text-gray-400 hover:text-primary transition-colors duration-300 text-left text-sm hover:translate-x-1 inline-block transition-transform"
                  >
                    <i className="fas fa-chevron-right text-xs mr-2 text-primary"></i>
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-lg">Get In Touch</h4>
            <div className="space-y-3">
              <div className="flex items-start text-gray-400 text-sm">
                <i className="fas fa-envelope mr-3 text-primary mt-1 flex-shrink-0"></i>
                <a 
                  href="mailto:kiratmehta92@gmail.com"
                  className="hover:text-primary transition-colors duration-300 break-words"
                >
                  kiratmehta92@gmail.com
                </a>
              </div>
              <div className="flex items-start text-gray-400 text-sm">
                <i className="fas fa-map-marker-alt mr-3 text-primary mt-1 flex-shrink-0"></i>
                <span>Available for Remote Work</span>
              </div>
              <div className="flex items-start text-gray-400 text-sm">
                <i className="fas fa-clock mr-3 text-primary mt-1 flex-shrink-0"></i>
                <span>Response: 24-48 hours</span>
              </div>
            </div>
          </div>
        </div>

        {/* Skills Tags */}
        <div className="py-6 border-t border-slate-800">
          <div className="flex flex-wrap gap-2 justify-center">
            {[
              'C#', 'SQL', 'React', 'TypeScript', 'JavaScript', 'Azure', 
              'Git', 'AI Tools', 'Problem Solving', 'Technical Support'
            ].map((skill, index) => (
              <span
                key={index}
                className="bg-slate-800/50 text-gray-400 px-3 py-1 rounded-full text-xs font-medium border border-slate-700/50 hover:border-primary/50 hover:text-primary transition-all duration-300"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-6 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Kirat Mehta. All rights reserved.
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-400">
              <span className="flex items-center">
                <i className="fab fa-react text-primary mr-1.5"></i>
                Built with React & TypeScript
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center">
                <i className="fas fa-palette text-primary mr-1.5"></i>
                Styled with TailwindCSS
              </span>
              <span className="hidden sm:inline">•</span>
              <button
                onClick={scrollToTop}
                className="hover:text-primary transition-colors duration-300 flex items-center"
              >
                <i className="fas fa-arrow-up mr-1"></i>
                Back to Top
              </button>
            </div>
          </div>
        </div>

        {/* Decorative Gradient Line */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent"></div>
      </div>

      {/* Floating Back to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-12 h-12 bg-primary hover:bg-primary/90 text-white rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-40 hover:shadow-primary/50"
        aria-label="Back to top"
      >
        <i className="fas fa-chevron-up"></i>
      </button>
    </footer>
  );
};

export default Footer;